import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'bem-cn';

import { useGetChapterQuery, useGetCourseQuery, useGetChaptersQuery } from '../../store/services/course';
import type { Chapter } from '../../store/services/course/types';
import { WebAppLayout } from '../../layouts/WebAppLayout';
import { HeaderElements } from '../../components/Header';
import { ChapterCard } from '../../components/ChapterCard';
import { BackButton } from '../../components/BackButton';
import { TagList } from '../../components/TagList';
import { Link, Stack } from '../../components/UI';
import { useNavigateBack } from '../../hooks';
import { ChapterPageProps } from './ChapterPage.types';

import './ChapterPage.scss';

const b = cn('chapter-page');

export const ChapterPage: React.FC<ChapterPageProps> = () => {
    const { courseId, chapterId } = useParams<{ courseId: string; chapterId: string }>();
    const numericCourseId = parseInt(courseId || '0', 10);
    const orderNumber = parseInt(chapterId || '0', 10); // chapterId теперь интерпретируется как orderNumber

    const handleBackButtonClick = useNavigateBack();

    const headerElements: HeaderElements = useMemo(
        () => ({
            desktop: {
                left: { type: 'logo' },
                right: { type: 'profile' },
            },
            mobile: {
                left: { type: 'backButton', onClick: handleBackButtonClick },
                right: { type: 'profile' },
            },
        }),
        [handleBackButtonClick],
    );

    const {
        data: chapter,
        isLoading: isChapterLoading,
        error: chapterError,
    } = useGetChapterQuery(
        { courseId: numericCourseId, orderNumber },
        { skip: !numericCourseId || !orderNumber || isNaN(numericCourseId) || isNaN(orderNumber) },
    );

    const {
        data: course,
        isLoading: isCourseLoading,
        error: courseError,
    } = useGetCourseQuery({ courseId: numericCourseId }, { skip: !numericCourseId || isNaN(numericCourseId) });

    const {
        data: chaptersData,
        isLoading: isChaptersLoading,
        error: chaptersError,
    } = useGetChaptersQuery({ courseId: numericCourseId }, { skip: !numericCourseId || isNaN(numericCourseId) });

    if (!numericCourseId || !orderNumber || isNaN(numericCourseId) || isNaN(orderNumber)) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('error')}>
                        <h1>Ошибка</h1>
                        <p>Некорректные параметры страницы</p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    if (isChapterLoading || isCourseLoading || isChaptersLoading) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('loading')}>
                        <h1>Загрузка...</h1>
                        <p>Получаем информацию о главе</p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    if (chapterError || courseError || chaptersError) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('error')}>
                        <h1>Ошибка</h1>
                        <p>Не удалось загрузить информацию о главе</p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    if (!chapter) {
        return (
            <WebAppLayout headerElements={headerElements}>
                <Stack className={b()}>
                    <div className={b('error')}>
                        <h1>Глава не найдена</h1>
                        <p>
                            Глава с номером {orderNumber} не существует в курсе {numericCourseId}
                        </p>
                    </div>
                </Stack>
            </WebAppLayout>
        );
    }

    const chapters = chaptersData?.chapters || [];

    // Создаем теги для главы
    const chapterTags = [`Глава ${orderNumber}`, chapter.chapter_time];

    return (
        <WebAppLayout headerElements={headerElements} hideNavigation>
            <Stack className={b()}>
                <div className={b('header')}>
                    <BackButton className={b('back-button')} />

                    <div className={b('course-info')}>
                        <div className={b('course-label')}>Сейчас вы на курсе:</div>
                        <div className={b('course-title')}>{course?.name || 'Загрузка...'}</div>
                    </div>
                </div>

                <div className={b('chapter-info')}>
                    <TagList tags={chapterTags} maxVisible={2} />
                    <h1 className={b('chapter-title')}>{chapter.name}</h1>
                    <p className={b('chapter-description')}>{chapter.description}</p>
                </div>

                {chapter.video_url && (
                    <div className={b('video-container')}>
                        <iframe
                            src={chapter.video_url}
                            className={b('video-player')}
                            title="Видео урок"
                            allowFullScreen
                            frameBorder="0"
                        />
                    </div>
                )}

                {chapters.length > 0 && (
                    <div className={b('chapters')}>
                        <h2 className={b('chapters-title')}>Программа курса</h2>
                        <div className={b('chapters-list')}>
                            {chapters.map((chapterItem: Chapter) => (
                                <Link
                                    key={chapterItem.id}
                                    to={`/course/${courseId}/chapter/${chapterItem.order_number}`}
                                >
                                    <ChapterCard chapter={chapterItem} className={b('chapter-item')} />
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </Stack>
        </WebAppLayout>
    );
};
